import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  Tooltip,
} from 'recharts';

const COLORS = {
  emailsReceived: '#5D29EC',
  mailingList: '#9A85D4',
  labelColor: '#616E7C',
  titleColor: '#1F2933',
  border: '#CBD2D9',
  dimmed: 0.35,
};

const STRIPE_HEIGHT = 4;
const STRIPE_GAP = 2;
const STRIPE_STEP = STRIPE_HEIGHT + STRIPE_GAP;
const BAR_COUNT = 48;

// Mock detailed data per week: [emailsReceived, mailingList, totalEmails, totalMailingLists, startDate]
interface WeekMeta {
  emails: number;
  mailingLists: number;
  totalEmails: number;
  totalLists: number;
  startDate: string;
  endDate: string;
  month: string;
  endMonth: string;
  year: number;
}

function generateWeekMeta(): WeekMeta[] {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const result: WeekMeta[] = [];
  // Start from Monday May 6, 2024 and span 48 weeks into April 2025.
  // Use Date constructor overflow (new Date(2024, 4, 40) → June 9)
  // to avoid DST timestamp drift
  const seeded = seedRandom(42);

  for (let w = 0; w < BAR_COUNT; w++) {
    const startDay = 6 + w * 7;
    const endDay = startDay + 6;
    const start = new Date(2024, 4, startDay);
    const end = new Date(2024, 4, endDay);
    const totalEmails = 15 + Math.floor(seeded() * 30);
    const totalLists = 3 + Math.floor(seeded() * 6);
    result.push({
      emails: finalData[w]?.[0] ?? 5,
      mailingLists: finalData[w]?.[1] ?? 5,
      totalEmails,
      totalLists,
      startDate: ordinal(start.getDate()),
      endDate: ordinal(end.getDate()),
      month: months[start.getMonth()],
      endMonth: months[end.getMonth()],
      year: start.getFullYear(),
    });
  }
  return result;
}

// Simple seeded PRNG so mock data is stable across renders
function seedRandom(seed: number) {
  return () => {
    seed = (seed * 16807 + 0) % 2147483647;
    return (seed - 1) / 2147483646;
  };
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// Final data: [emailsReceived, mailingList] as stripe counts
const finalData: [number, number][] = [
  [0, 7], [7, 7], [4, 5], [7, 7], [8, 6], [7, 7], [7, 7], [5, 5],
  [7, 7], [7, 7], [5, 6], [4, 7], [7, 7], [5, 7], [6, 5], [5, 7],
  [4, 6], [5, 6], [7, 6], [5, 6], [4, 6], [5, 5], [7, 6], [5, 6],
  [7, 5], [5, 7], [7, 6], [7, 6], [6, 5], [6, 6], [7, 5], [5, 7],
  [5, 6], [7, 7], [6, 6], [7, 5], [6, 6], [7, 6], [6, 5], [7, 6],
  [7, 6], [8, 6], [7, 5], [6, 7], [6, 6], [6, 7], [7, 6], [8, 7],
];

const weekMeta = generateWeekMeta();

const WAVE_DURATION = 950;
const WAVE_CYCLES = 2;
const SETTLE_DURATION = 550;
const TOTAL_LOADING = WAVE_DURATION * WAVE_CYCLES + SETTLE_DURATION;

function generateWaveData(time: number, amplitude: number = 1): [number, number][] {
  const result: [number, number][] = [];
  for (let i = 0; i < BAR_COUNT; i++) {
    const phase1 = (i / BAR_COUNT) * Math.PI * 3 - time * 0.002;
    const phase2 = (i / BAR_COUNT) * Math.PI * 1.8 + time * 0.0015;
    const phase3 = (i / BAR_COUNT) * Math.PI * 5 - time * 0.003;
    const wave1 = Math.sin(phase1);
    const wave2 = Math.sin(phase2) * 0.4;
    const wave3 = Math.sin(phase3) * 0.15;
    const combined = ((wave1 + wave2 + wave3) / 1.55) * amplitude;
    const total = Math.round(7 + combined * 5);
    const emailRatio = 0.5 + Math.sin(phase1 * 0.5 + 0.3) * 0.2;
    const emails = Math.max(0, Math.min(total, Math.round(total * emailRatio)));
    const mailing = total - emails;
    result.push([emails, mailing]);
  }
  return result;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

const MONTH_LABELS: Record<number, string> = {
  0: 'MAY', 4: 'JUN', 8: 'JUL', 12: 'AUG', 16: 'SEP', 20: 'OCT',
  24: 'NOV', 28: 'DEC', 32: 'JAN', 36: 'FEB', 40: 'MAR', 44: 'APR',
};

function buildChartData(weeklyData: [number, number][]) {
  return weeklyData.map(([emails, mailing], i) => ({
    week: i,
    emailsReceived: emails,
    mailingList: mailing,
    total: emails + mailing,
  }));
}

const CustomXAxisTick = (props: any) => {
  const { x, y, payload } = props;
  const label = MONTH_LABELS[payload.value];
  if (!label) return null;
  return (
    <text
      x={x}
      y={y + 14}
      textAnchor="middle"
      fontSize={12}
      fontWeight={500}
      fill={COLORS.labelColor}
      fontFamily="'Inter', -apple-system, sans-serif"
    >
      {label}
    </text>
  );
};

const CustomBarShape = (props: any) => {
  const { x, y, width, height, payload, hoveredIndex } = props;
  if (!height || height <= 0) return null;

  const { emailsReceived, mailingList } = payload;
  const barIndex = props.index;
  const totalStripes = emailsReceived + mailingList;
  const totalVisualHeight = totalStripes * STRIPE_STEP - STRIPE_GAP;
  const barBottom = y + height;
  const barTop = barBottom - totalVisualHeight;

  const isDimmed = hoveredIndex !== null && Number(hoveredIndex) !== Number(barIndex);

  const stripes: React.ReactElement[] = [];

  for (let i = 0; i < totalStripes; i++) {
    const stripeY = barTop + i * STRIPE_STEP;
    const isEmail = i < emailsReceived;
    stripes.push(
      <rect
        key={i}
        x={x}
        y={stripeY}
        width={width}
        height={STRIPE_HEIGHT}
        rx={1}
        fill={isEmail ? COLORS.emailsReceived : COLORS.mailingList}
      />
    );
  }

  return (
    <g opacity={isDimmed ? 0.3 : 1}>
      {stripes}
    </g>
  );
};

const CustomTooltipContent = ({ active, payload }: any) => {
  if (!active || !payload || !payload[0]) return null;

  const weekIndex = payload[0].payload.week;
  const meta = weekMeta[weekIndex];
  if (!meta) return null;

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '10px',
        padding: '14px 16px',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.10), 0 1px 4px rgba(0, 0, 0, 0.06)',
        border: `1px solid #E4E7EB`,
        minWidth: '220px',
        fontFamily: "'Inter', -apple-system, sans-serif",
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: COLORS.emailsReceived, flexShrink: 0 }} />
        <span style={{ fontSize: 14, color: COLORS.titleColor }}>
          You received <strong>{meta.totalEmails}</strong> Emails
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: COLORS.mailingList, flexShrink: 0 }} />
        <span style={{ fontSize: 14, color: COLORS.titleColor }}>
          Sent by <strong>{meta.totalLists}</strong> Mailing Lists
        </span>
      </div>
      <div style={{ fontSize: 13, color: '#9AA5B4' }}>
        From {meta.startDate}{meta.month !== meta.endMonth ? ` of ${meta.month}` : ''} to {meta.endDate} of {meta.endMonth} {meta.year}
      </div>
    </div>
  );
};

const SpeakerphoneIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 5.88218V19.2402C11 20.2121 10.2121 21 9.24018 21C8.49646 21 7.83302 20.5325 7.58288 19.8321L5.43647 13.6829M18 13C19.6569 13 21 11.6569 21 10C21 8.34315 19.6569 7 18 7M5.43647 13.6829C4.0043 13.0741 3 11.6543 3 10C3 7.79086 4.79086 6 6.99999 6H8.83208C12.9327 6 16.4569 4.7659 18 3L18 17C16.4569 15.2341 12.9327 14 8.83208 14L6.99998 14C6.44518 14 5.91677 13.887 5.43647 13.6829Z" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export const MailingListsNoiseChart: React.FC = () => {
  const [chartData, setChartData] = useState(() => buildChartData(generateWaveData(0)));
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const lastWaveDataRef = useRef<[number, number][]>(generateWaveData(0));
  const rafRef = useRef<number>(0);

  const animate = useCallback((timestamp: number) => {
    if (!startTimeRef.current) startTimeRef.current = timestamp;
    const elapsed = timestamp - startTimeRef.current;

    if (elapsed < WAVE_DURATION * WAVE_CYCLES) {
      // Dampen amplitude during the last 40% of the wave phase so the
      // transition into the settle phase is less abrupt.
      const waveProgress = elapsed / (WAVE_DURATION * WAVE_CYCLES);
      const dampStart = 0.6;
      const amplitude = waveProgress < dampStart
        ? 1
        : 1 - Math.pow((waveProgress - dampStart) / (1 - dampStart), 2) * 0.55;
      const waveData = generateWaveData(elapsed, amplitude);
      lastWaveDataRef.current = waveData;
      setChartData(buildChartData(waveData));
      rafRef.current = requestAnimationFrame(animate);
    } else if (elapsed < TOTAL_LOADING) {
      const settleElapsed = elapsed - WAVE_DURATION * WAVE_CYCLES;
      const t = easeOutCubic(Math.min(1, settleElapsed / SETTLE_DURATION));
      const lastWave = lastWaveDataRef.current;

      const blended: [number, number][] = finalData.map(([fe, fm], i) => {
        const [we, wm] = lastWave[i] || [fe, fm];
        return [
          Math.round(lerp(we, fe, t)),
          Math.round(lerp(wm, fm, t)),
        ];
      });

      setChartData(buildChartData(blended));
      rafRef.current = requestAnimationFrame(animate);
    } else {
      setChartData(buildChartData(finalData));
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [animate]);

  const handleChartMouseMove = useCallback((state: any) => {
    if (isLoading) return;
    // recharts v3 uses different property names; check multiple
    const idx = state?.activeTooltipIndex ?? state?.activeIndex ?? state?.activeLabel;
    if (idx != null) {
      setHoveredIndex(Number(idx));
    }
  }, [isLoading]);

  const handleChartMouseLeave = useCallback(() => {
    setHoveredIndex(null);
  }, []);

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '8px',
        border: `1px solid ${COLORS.border}`,
        padding: '20px 24px 16px',
        width: '100%',
        maxWidth: 'var(--chart-max-width, 1180px)',
        boxSizing: 'border-box' as const,
      }}
    >
      <style>{`
        .recharts-bar-rectangle g {
          transition: opacity 0.25s ease;
        }
      `}</style>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <SpeakerphoneIcon />
          <span
            style={{
              fontSize: '16px',
              fontWeight: 400,
              color: COLORS.titleColor,
              letterSpacing: '-0.01em',
            }}
          >
            Mailing Lists noise
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <LegendItem color={COLORS.emailsReceived} label="Emails received" />
          <LegendItem color={COLORS.mailingList} label="Mailing List" />
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={140}>
        <BarChart
          data={chartData}
          margin={{ top: 4, right: 16, bottom: 0, left: 16 }}
          barCategoryGap="18%"
          onMouseMove={handleChartMouseMove}
          onMouseLeave={handleChartMouseLeave}
        >
          <XAxis
            dataKey="week"
            axisLine={false}
            tickLine={false}
            tick={<CustomXAxisTick />}
            interval={0}
          />
          <YAxis hide domain={[0, 18]} />
          {!isLoading && (
            <Tooltip
              content={<CustomTooltipContent />}
              cursor={false}
              allowEscapeViewBox={{ x: true, y: true }}
              offset={20}
              isAnimationActive={false}
            />
          )}
          <Bar
            dataKey="total"
            shape={(props: any) => <CustomBarShape {...props} hoveredIndex={hoveredIndex} />}
            isAnimationActive={false}
            onMouseEnter={(_: any, index: number) => { if (!isLoading) setHoveredIndex(index); }}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {chartData.map((_, index) => (
              <Cell key={index} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const LegendItem: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
    <div
      style={{
        width: 8,
        height: 8,
        borderRadius: 2,
        backgroundColor: color,
      }}
    />
    <span style={{ fontSize: 12, color: COLORS.labelColor, fontWeight: 500 }}>
      {label}
    </span>
  </div>
);
